import { DataHandlerSnapshot, DataHandlerDevice, DeviceGroup, Device } from "luxedo-data"

import type { CurrentUserDataGetter, DeviceDataGetter, DeviceData } from "@lux-ai/client"

import { LUX_AI_PRIV } from "../../../types/consts"
import { getCurrentUser } from "../../../stores/UserStore.svelte"
import { getSubscriptionManager } from "../../../modules/subscription-management/SubscriptionManager.svelte"

function aggregateDevice(deviceId: number): DeviceData {
	const dev = DataHandlerDevice.get(deviceId)
	const snapshots = DataHandlerSnapshot.getMany(
		undefined,
		(snap) =>
			snap.dev_id === dev.id && snap.resolution.w === dev.resX && snap.resolution.h === dev.resY,
	)

	return {
		id: dev.id.toString(),
		name: dev.name,
		snapshots: snapshots.map((snap) => {
			return {
				id: snap.id.toString(),
				src: snap.src,
				edited: snap.edited,
			}
		}),
	}
}

export const userDataAdapter: CurrentUserDataGetter = {
	getCurrentUserData: () => {
		const user = getCurrentUser()
		const subscriptionManager = getSubscriptionManager()
		const luxAiSubscription = subscriptionManager.subscriptions.find(
			(sub) => sub.type === "LUX_AI_ANNUAL",
		)

		return {
			isAISuperUser: user.hasPriv(LUX_AI_PRIV),
			isSubscribed: !!luxAiSubscription,
			totalTokens: luxAiSubscription?.totalUnits ?? 0,
			availableTokens: luxAiSubscription?.availableUnits ?? 0,
		}
	},

	refreshCurrentUserData: async () => {
		const subscriptionManager = getSubscriptionManager()
		subscriptionManager.refreshUserSubscriptions()
	},
}

export const deviceDataGetter: DeviceDataGetter = {
	getDevice: (deviceId: string) => {
		return aggregateDevice(Number(deviceId))
	},

	// Function that returns a list of compatible devices, i.e. not device groups
	listCompatibleDevices: () => {
		const devices = DataHandlerDevice.getMany(undefined, (dev) => !(dev instanceof DeviceGroup))

		return devices.map((dev) => aggregateDevice(dev.id))
	},

	getSnapshotById: (snapshotId: string) => {
		const snap = DataHandlerSnapshot.get(Number(snapshotId))
		return {
			id: snap.id.toString(),
			src: snap.src,
			edited: snap.edited,
		}
	},

	refreshSnapshots: async (deviceId: string) => {
		DataHandlerSnapshot.pull()
	},
}
